import { createElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Loading from 'components/global/Loading';
import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useChangeLanguage from 'hooks/useChangeLanguage';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';

import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';

import AddressVerification1, { addressVerification1DefaultValues } from './AddressVerification1';
import AddressVerification2 from './AddressVerification2';
import BulletConfirmation from './BulletConfirmation';
import DeliverySlotSelection, { deliverySlotSelectionDefaultValues } from './DeliverySlotSelection';
import Feedback1, { feedback1DefaultValues } from './Feedback1';
import Feedback2 from './Feedback2';
import Feedback3 from './Feedback3';
import LinkExpired from './LinkExpired';
import OrderConfirmation, { orderConfirmationDefaultValues } from './OrderConfirmation';
import OrderTracking1, { orderTracking1DefaultValues } from './OrderTracking1';
import OrderTracking2 from './OrderTracking2';
import Reschedule, { rescheduleDefaultValues } from './Reschedule';
import ReturnRequest1, { returnRequest1DefaultValues } from './ReturnRequest1';
import ReturnRequest2 from './ReturnRequest2';
import SmsAds from './SmsAds';
import SmsFooter from './SmsFooter';
import UndeliveredAttempt1, { undeliveredAttempt1DefaultValues } from './UndeliveredAttempt1';
import UndeliveredAttempt2 from './UndeliveredAttempt2';
import * as S from './styled/SmsLayout.styled';

/**
 * @author Burak Ak
 */

export const defaultValues = {
	customerName: 'Burak',
	orderNumber: '123456789',
	storeName: 'OTO',
	deliveryCompany: 'Aramex',
	deliveryCompanyAr: 'أرامكس',
	deliveryCompanyLogo: 'https://storage.googleapis.com/tryoto-public/delivery-logo/aramex.png',
	orderDate: '01.01.2021',
	trackingNumber: '123456789',
	trackingURL: 'https://www.aramex.com.tr',
	status: 'returned',
	deliveryDate: '01.01.2021',
	deliveryNote: 'Please leave the package at the door',
	address: 'Istanbul, Turkey',
	street: 'Istanbul, Turkey',
	city: 'Istanbul',
	lat: 41.0082,
	lon: 28.9784,
	useForAllAddress: true,
	language: 'english',
	returnDeliveryCompanyName: 'Aramex',
	returnShippingNumber: '123456789',
	returnApprovalStatus: 'approved',
	totalAmount: 1234,
	orderConfirmationStatus: 'notConfirmedYet',
	currency: 'SAR',
	defaultValues: true,
	result: {
		Brand: 'apple',
		deliverySlotGroupId: 40,
		AvailableSlotsArray: [
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '09:00',
				endTime: '09:30',
				capacity: 15
			},
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '12:00',
				endTime: '12:30',
				capacity: 15
			},
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '12:30',
				endTime: '13:00',
				capacity: 15
			},
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '13:00',
				endTime: '13:30',
				capacity: 15
			},
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '17:30',
				endTime: '18:00',
				capacity: 15
			},
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '18:00',
				endTime: '18:30',
				capacity: 15
			},
			{
				date: '2024-10-01',
				deliverySlotGroupId: 39,
				startTime: '18:30',
				endTime: '19:00',
				capacity: 15
			},
			{
				date: '2024-10-02',
				deliverySlotGroupId: 39,
				startTime: '09:00',
				endTime: '09:30',
				capacity: 15
			},
			{
				date: '2024-10-02',
				deliverySlotGroupId: 39,
				startTime: '09:30',
				endTime: '10:00',
				capacity: 15
			},
			{
				date: '2024-10-03',
				deliverySlotGroupId: 39,
				startTime: '17:00',
				endTime: '17:30',
				capacity: 15
			},
			{
				date: '2024-10-03',
				deliverySlotGroupId: 39,
				startTime: '17:30',
				endTime: '18:00',
				capacity: 15
			},

			{
				date: '2024-10-04',
				deliverySlotGroupId: 39,
				startTime: '12:00',
				endTime: '12:30',
				capacity: 15
			},
			{
				date: '2024-10-04',
				deliverySlotGroupId: 39,
				startTime: '12:30',
				endTime: '13:00',
				capacity: 15
			},
			{
				date: '2024-10-04',
				deliverySlotGroupId: 39,
				startTime: '13:00',
				endTime: '13:30',
				capacity: 15
			}
		],
		OrderItemsArray: [
			{
				Quantity: 1,
				ItemName: 'iphone 16',
				Image: 'https://storage.googleapis.com/download/storage/v1/b/oto-files-stage/o/files%2F24339%2Fimport%2F2bb7c873-19d1-4357-88f5-a018fa729fc6.jpg?generation=1726491197662842&alt=media',
				SKU: '10'
			},
			{
				Quantity: 1,
				ItemName: 'iphone 16',
				Image: 'https://storage.googleapis.com/download/storage/v1/b/oto-files-stage/o/files%2F24339%2Fimport%2F2bb7c873-19d1-4357-88f5-a018fa729fc6.jpg?generation=1726491197662842&alt=media',
				SKU: '10'
			},
			{
				Quantity: 1,
				ItemName: 'iphone 16',
				Image: 'https://storage.googleapis.com/download/storage/v1/b/oto-files-stage/o/files%2F24339%2Fimport%2F2bb7c873-19d1-4357-88f5-a018fa729fc6.jpg?generation=1726491197662842&alt=media',
				SKU: '10'
			}
		],
		OrderID: 'OID-34767-1023',
		DeliveryCompanyLogo: 'https://storage.googleapis.com/tryoto-public/delivery-logo/otoflex.png',
		DeliveryCompanyName: 'OTO Flex',
		OrderDate: '2024-09-16T12:54:37Z'
	},
	orderItems: [
		{
			productImage:
				'https://storage.googleapis.com/download/storage/v1/b/oto-files-stage/o/files%2F6030%2Fimport%2F516637bb-91c0-4a43-83fb-3541f2c12f2c.jpeg?generation=1681826055765503&alt=media',
			qtyOrdered: 2,
			ID: 4815003,
			productName: 'Iphone 14 Pro 256gb Deep Purple'
		}
	],
	downloadAWB:
		'https://login.tryoto.com/FileDownload?fullPath=alpISStHdURBMm0wSDkxMDJzUFd3M1Y0d2wzVkRGSmtYQitJSGtFT1pLbGRhVE4rZm9ZV2N3PT0=',
	advertisements: [
		{
			advImage: 'https://login.tryoto.com/Image?imagePath=FwMdCwarLd5yihkA%2B%2FYMdUe8mJFd1vi73coBIKBIZmg%3D',
			captionText: 'test',
			advLink: 'www.google.com'
		},
		{
			advImage:
				'https://login.tryoto.com/Image?imagePath=FwMdCwarLd67NZBeE2bb4hy5UUKBSlvIfvLBuJP1uV3B%2BWCedpBOWw%3D%3D',
			captionText: 'test2',
			advLink: 'www.tryoto.com'
		}
	]
};

const SmsPaths = {
	'/sms/order-tracking': OPEN.SMS.GET_TRACKING_SMS_DETAILS,
	'/sms/address-verification': OPEN.SMS.GET_ADDRESS_VERIFICATION_SMS_DETAILS,
	'/sms/feedback': OPEN.SMS.GET_FEEDBACK_SMS_DETAILS,
	'/sms/undelivered-attempt': OPEN.SMS.GET_UNDELIVERED_ATTEMPT_SMS_DETAILS,
	'/sms/return-request': OPEN.SMS.GET_RETURN_REQUEST_SMS_DETAILS,
	'/sms/order-confirmation': OPEN.SMS.GET_ORDER_CONFIRMATION_SMS_DETAILS,
	'/sms/reschedule': OPEN.SMS.GET_RESCHEDULE_SMS_DETAILS,
	'/sms/delivery-slot': OPEN.SMS.GET_DELIVERY_SLOT_DETAILS,
	'/sms/bullet-confirmation': OPEN.SMS.GET_BULLET_CONFIRMATION_SMS_DETAILS
};

const SmsComponentsDefaultValues = {
	'/sms/order-tracking': orderTracking1DefaultValues,
	'/sms/address-verification': addressVerification1DefaultValues,
	'/sms/feedback': feedback1DefaultValues,
	'/sms/undelivered-attempt': undeliveredAttempt1DefaultValues,
	'/sms/return-request': returnRequest1DefaultValues,
	'/sms/order-confirmation': orderConfirmationDefaultValues,
	'/sms/reschedule': rescheduleDefaultValues,
	'/sms/delivery-slot': deliverySlotSelectionDefaultValues
};

const SmsLayoutWrapper = () => {
	const { pathname } = useLocation();
	const changeLanguage = useChangeLanguage();
	const { showError } = useAlert();
	const [data, setData] = useState(defaultValues);
	const navigate = useNavigate();
	const [templateName, setTemplateName] = useState('template1');
	const [loading, setLoading] = useState(true);

	const [searchParams] = useSearchParams();

	const getSMSDetails = async (URL, key, isFirstSubmit) => {
		try {
			const response = await openAxios.post(URL, {
				key
			});
			const fetchedPropJson = response.data.templatePropJSON
				? response.data.templatePropJSON
				: JSON.stringify({
						propJson: SmsComponentsDefaultValues[pathname],
						templateName: 'template1'
					});

			const templatePropJSON = JSON.parse(fetchedPropJson);

			await changeLanguage(response.data.language);
			if (!isFirstSubmit) {
				setTemplateName(response.data?.code === 'error.SMS010' ? 'expiryLink' : templatePropJSON?.templateName);
			}
			setData({ ...response.data, templatePropJSON });
			setLoading(false);
		} catch (error) {
			navigate(PATHS.SMS_ERROR);
			AppLogger.error(error);
			showError();
		}
	};

	const getData = async () => {
		const serviceURL = SmsPaths[pathname];
		const key = searchParams.get('key');
		if (key) {
			getSMSDetails(serviceURL, key, true);
		} else {
			setLoading(false);
		}
	};

	useEffect(() => {
		const serviceURL = SmsPaths[pathname];
		if (serviceURL) {
			const key = searchParams.get('key');
			if (key) {
				getSMSDetails(serviceURL, key);
			} else {
				setLoading(false);
			}
		} else {
			navigate(PATHS.SMS_ERROR);
		}
	}, [pathname]);

	return loading ? (
		<Loading />
	) : (
		<SmsLayout data={data} pathname={pathname} templateName={templateName} getData={getData} />
	);
};

export default SmsLayoutWrapper;

export const SmsLayout = ({ pathname, templateName, data, isMobile, getData }) => {
	// Hooks and states declaration
	const [configData, setConfigData] = useState(null);
	const changeLanguage = useChangeLanguage();
	const [language, setLanguage] = useState();
	const [languageMenuButtonRef, setLanguageMenuButtonRef] = useState(null);

	const localization = useSelector((state) => state.layout.localization);

	useEffect(() => {
		if (data) {
			setLanguage(data.language);
			const dummyData = {};
			data?.templatePropJSON?.propJson?.forEach((el) => (dummyData[el.name] = el.value));
			setConfigData(dummyData);
		}
	}, [data]);

	const SmsComponents = useMemo(
		() => ({
			'/sms/order-tracking': {
				template1: OrderTracking1,
				template2: OrderTracking2,
				expiryLink: LinkExpired
			},
			'/sms/address-verification': {
				template1: AddressVerification1,
				template2: AddressVerification2,
				expiryLink: LinkExpired
			},
			'/sms/feedback': {
				template1: Feedback1,
				template2: Feedback2,
				template3: Feedback3,
				expiryLink: LinkExpired
			},
			'/sms/delivery-slot': {
				template1: DeliverySlotSelection
			},
			'/sms/undelivered-attempt': {
				template1: UndeliveredAttempt1,
				template2: UndeliveredAttempt2,
				expiryLink: LinkExpired
			},
			'/sms/return-request': {
				template1: ReturnRequest1,
				template2: ReturnRequest2,
				expiryLink: LinkExpired
			},
			'/sms/order-confirmation': {
				template1: OrderConfirmation,
				expiryLink: LinkExpired
			},
			'/sms/reschedule': {
				template1: Reschedule,
				expiryLink: LinkExpired
			},
			'/sms/bullet-confirmation': {
				template1: BulletConfirmation,
				expiryLink: LinkExpired
			}
		}),
		[]
	);

	return (
		<S.Container pageBackgroundColor={configData?.pageBackgroundColor}>
			<S.NavBar navigationBackgroundColor={configData?.navigationBackgroundColor}>
				<S.NavBarContent>
					<Image
						width={'4.5rem'}
						height={'4.5rem'}
						url={
							data.companyLogo
								? data.companyLogo
								: 'https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png'
						}
					/>
					<Button variant="borderless" ref={setLanguageMenuButtonRef}>
						<Image url={localization.icon} width="20px" height="15px" />
						<Typography>{localization.name}</Typography>
						<Icon icon={faAngleDown} />
					</Button>
					<Menu
						buttonRef={languageMenuButtonRef}
						menuItems={languages.map((language) => ({
							label: language.name,
							img: language.icon,
							imgProps: { width: '20px', height: '15px' },
							action: () => {
								changeLanguage(language.code);
								setLanguage(language.code);
							}
						}))}
					/>
				</S.NavBarContent>
			</S.NavBar>

			<S.ScrollContainer>
				<S.Content isMobile={isMobile}>
					{configData?.showAdvertisementOnTop && (
						<SmsAds advertisements={data.advertisements} language={language} />
					)}

					{configData && (
						<S.ContentWrapper className={isMobile ? 'mobileView' : ''}>
							{createElement(SmsComponents[pathname][templateName], {
								...data,
								isMobile,
								language,
								propJson: configData,
								getData
							})}
						</S.ContentWrapper>
					)}

					{configData?.showAdvertisementOnBottom && (
						<SmsAds advertisements={data.advertisements} language={language} />
					)}
				</S.Content>

				<SmsFooter />
			</S.ScrollContainer>
		</S.Container>
	);
};
